// apps/frontend/src/pages/dealer-kit/stock-search/AskAiBar.tsx
import { useState } from 'react';

import { ArrowRight, Loader2, Mic, Sparkles, Square, Undo2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useVoiceNote } from '@/hooks/useVoiceNote';
import { cn } from '@/lib/utils';

interface Props {
  onAsk: (message: string) => Promise<void>;
  busy: boolean;
  summary: string | null;
  canUndo: boolean;
  onUndo: () => void;
  error: string | null;
}

export const AskAiBar = ({ onAsk, busy, summary, canUndo, onUndo, error }: Props) => {
  const [text, setText] = useState('');
  const voice = useVoiceNote({ onText: (t) => setText(t) });
  const recording = voice.status === 'recording';
  const transcribing = voice.status === 'transcribing';

  const send = async () => {
    const message = text.trim();
    if (!message || busy) return;
    await onAsk(message);
  };

  const status = busy
    ? 'Searching stock…'
    : transcribing
      ? 'Transcribing voice note…'
      : recording
        ? 'Recording… (tap stop when done)'
        : summary;

  const micMessage =
    voice.status === 'blocked'
      ? 'Microphone blocked — allow it in the browser to use voice notes'
      : voice.status === 'unsupported'
        ? 'Voice notes are not supported in this browser'
        : voice.error;

  return (
    <div className="bg-card rounded-xl border p-3 shadow-sm">
      <form
        className="flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <Sparkles className="text-primary ml-1 h-5 w-5 shrink-0" aria-hidden />
        <label htmlFor="ask-ai" className="sr-only">
          Describe the car you are looking for
        </label>
        <input
          id="ask-ai"
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder='Describe the car… e.g. "automatic SUV under 12k near B23, low miles"'
          className="placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent py-2 text-base outline-none"
          disabled={recording}
        />
        <Button
          type="button"
          size="icon"
          variant={recording ? 'destructive' : 'ghost'}
          aria-label={recording ? 'Stop recording' : 'Record a voice note'}
          aria-pressed={recording}
          disabled={
            !voice.supported || voice.status === 'blocked' || voice.starting || transcribing || busy
          }
          onClick={() => (recording ? voice.stop() : void voice.start())}
          className={cn(recording && 'motion-safe:animate-pulse')}
        >
          {recording ? (
            <Square className="h-4 w-4" aria-hidden />
          ) : transcribing ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Mic className="h-4 w-4" aria-hidden />
          )}
        </Button>
        <Button
          type="submit"
          size="icon"
          aria-label="Ask AI"
          disabled={!text.trim() || busy || recording}
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <ArrowRight className="h-4 w-4" aria-hidden />
          )}
        </Button>
      </form>
      <div className="mt-2 flex min-h-6 items-center justify-between gap-2 px-1">
        <p aria-live="polite" className="text-muted-foreground text-sm">
          {status && (
            <>
              <span className="text-foreground font-medium">AI:</span> {status}
            </>
          )}
        </p>
        {recording && (
          <span aria-hidden className="text-muted-foreground text-sm tabular-nums">
            0:{String(voice.seconds).padStart(2, '0')}
          </span>
        )}
        {canUndo && !busy && (
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onUndo}>
            <Undo2 className="mr-1 h-3 w-3" aria-hidden />
            Undo AI
          </Button>
        )}
      </div>
      {(error || micMessage) && (
        <p role="alert" className="text-destructive mt-1 px-1 text-sm">
          {error ?? micMessage}
        </p>
      )}
    </div>
  );
};
